import type { LocalStorageKey } from './keys';
import { LocalStorageKeyFactory } from './keys';

/**
 * Storage policies for determining what gets cleared on logout
 */
export enum StoragePolicy {
  CLEAR_ON_LOGOUT = 'clear_on_logout',
  PERSIST_USER_SCOPED = 'persist_user_scoped',
  PERSIST_GLOBAL = 'persist_global',
}

/**
 * Map of keys/regex patterns to their storage policies
 * 
 * Order matters - more specific patterns should come before general ones.
 * The function will check exact key matches first, then regex patterns.
 */
export const KEY_POLICIES = new Map<LocalStorageKey | RegExp, StoragePolicy>([
  // Auth keys - always clear on logout
  [LocalStorageKeyFactory.AUTH_TOKEN, StoragePolicy.CLEAR_ON_LOGOUT],
  [LocalStorageKeyFactory.AUTH_TOKEN_EXPIRATION, StoragePolicy.CLEAR_ON_LOGOUT],
  [LocalStorageKeyFactory.USER_EMAIL, StoragePolicy.CLEAR_ON_LOGOUT],
  [LocalStorageKeyFactory.USER_ID, StoragePolicy.CLEAR_ON_LOGOUT],

  // DataGrid keys - user-scoped, persist (regex pattern to match all variations including versioned keys, filter presets, and selected preset)
  [/_mui_data_grid_storage_key(_v\d+)?(_filter_presets|_selected_preset)?$/, StoragePolicy.PERSIST_USER_SCOPED],

  // Report settings - global, persist (user preferences for report generation)
  [/^(inspection|issues?|issue)_report_/, StoragePolicy.PERSIST_GLOBAL],

  // Issue filters and parameters - user-scoped, persist
  [/_issue_filters$/, StoragePolicy.PERSIST_USER_SCOPED],
  [/_archived$/, StoragePolicy.PERSIST_USER_SCOPED],
  [/^issue_get_parameters_/, StoragePolicy.PERSIST_USER_SCOPED],

  // Navigation and search - user-scoped, persist (user's last position/search)
  [/_project_navigation_tab$/, StoragePolicy.PERSIST_USER_SCOPED],
  [/_search_phrase$/, StoragePolicy.PERSIST_USER_SCOPED],
  [/^viewed_template_/, StoragePolicy.PERSIST_USER_SCOPED],
  [/^template_search_key$/, StoragePolicy.PERSIST_GLOBAL],

  // Documentation preferences - user-scoped, persist
  [/^documentation_issues_visible_/, StoragePolicy.PERSIST_USER_SCOPED],
  [/^documentation_progress_active_/, StoragePolicy.PERSIST_USER_SCOPED],

  // Autocomplete suggestions - global, persist
  [/^autocomplete-/, StoragePolicy.PERSIST_GLOBAL],

  // Cache keys - clear on logout (should be refreshed)
  [/^data_/, StoragePolicy.CLEAR_ON_LOGOUT],
  [/^cache_id_/, StoragePolicy.CLEAR_ON_LOGOUT],
  [/^last_update_/, StoragePolicy.CLEAR_ON_LOGOUT],

  // Session state - clear on logout
  [/^hadChosenProject$/, StoragePolicy.CLEAR_ON_LOGOUT],

  // Service Worker flags - global, persist
  [LocalStorageKeyFactory.OLD_SW_CLEANED_FLAG, StoragePolicy.PERSIST_GLOBAL],
  [LocalStorageKeyFactory.SW_CONFIG_PROJECT_ID, StoragePolicy.PERSIST_GLOBAL],

  // Mobile app snackbar - global, persist
  [LocalStorageKeyFactory.CLOSED_INSTALL_MOBILE_APP_SNACKBAR_DATE, StoragePolicy.PERSIST_GLOBAL],

  // Persisted store keys (e.g. UiStore locale under persist:ui) - persist globally
  [/^persist:.*$/, StoragePolicy.PERSIST_GLOBAL],

  // Note: By default, if a key is not explicitly listed here,
  // it will be cleared on logout (safe default for security)
]);

/**
 * Finds the storage policy for a given key
 * Checks exact matches first, then regex patterns
 * 
 * @param key - The localStorage key to check
 * @returns The policy or null if not found
 */
function findPolicyForKey(key: string): StoragePolicy | null {
  // First check for exact key matches
  for (const [pattern, policy] of KEY_POLICIES.entries()) {
    if (typeof pattern === 'string' && pattern === key) {
      return policy;
    }
  }

  // Then check regex patterns
  for (const [pattern, policy] of KEY_POLICIES.entries()) {
    if (pattern instanceof RegExp && pattern.test(key)) {
      return policy;
    }
  }

  return null;
}

/**
 * Determines if a key should be cleared on logout
 * 
 * @param key - The localStorage key to check
 * @returns true if the key should be cleared, false if it should be persisted
 */
export function shouldClearOnLogout(key: string): boolean {
  const policy = findPolicyForKey(key);
  // Default: clear if not explicitly marked as persist
  return policy === null || policy === StoragePolicy.CLEAR_ON_LOGOUT;
}

/**
 * Gets the storage policy for a given key
 * 
 * @param key - The localStorage key to check
 * @returns The storage policy, or CLEAR_ON_LOGOUT as default
 */
export function getStoragePolicy(key: string): StoragePolicy {
  const policy = findPolicyForKey(key);
  // Default: clear on logout if not explicitly marked
  return policy ?? StoragePolicy.CLEAR_ON_LOGOUT;
}

